import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RegSkeleton } from "../../components/Skeletons";
import { registrationService } from "../../api/services/registrationService";
import { authService } from "../../api/services/authService";
import { AuthLayout } from "../../components/layouts";

export default function Register() {
    const navigate = useNavigate();

    // stages: 1 = данные (registration form), 2 = подтверждение (email code), 3 = опросник (quiz)
    const [stage, setStage] = useState(1);

    const [questions, setQuestions] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [correctAnswers, setCorrectAnswers] = useState(0);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isRegisterSubmitting, setRegisterSubmitting] = useState(false);
    const [isCodeSubmitting, setCodeSubmitting] = useState(false);
    const [isQuizSubmitting, setQuizSubmitting] = useState(false);
    const [isResending, setResending] = useState(false);

    const [testError, setTestError] = useState(false);
    const [regError, setRegError] = useState(false);

    const [ip, setIp] = useState("");
    const [ucpName, setUcpName] = useState("");
    const [targetTimer, setTargetTimer] = useState(60);
    const [email, setEmail] = useState("");

    const question = useMemo(
        () => (Array.isArray(questions) ? questions[currentQuestion] : null),
        [questions, currentQuestion],
    );

    useEffect(() => {
        const fetchQuestions = async () => {
            setLoading(true);
            try {
                const data = await registrationService.getQuestions();
                setQuestions(data?.questions || []);
            } catch (err) {
                setError(err.message || "Ошибка загрузки вопросов");
            } finally {
                setLoading(false);
            }
        };

        fetchQuestions();

        const loadIp = async () => {
            try {
                const clientIp = await authService.getIp();
                setIp(clientIp);
            } catch (e) {
                console.error("Ошибка при получении IP:", e);
            }
        };
        loadIp();
    }, []);

    const handleChangeUcpName = (e) => {
        const value = e.target.value;
        const cleaned = value.replace(/[^a-zA-Z0-9]/g, "");
        if (cleaned.length <= 20) setUcpName(cleaned);
    };

    const questionSubmit = async (event) => {
        event.preventDefault();
        if (!question) return;

        const formData = new FormData(event.target);
        const payload = Object.fromEntries(formData.entries());
        const answer = payload.answer;

        try {
            setQuizSubmitting(true);
            const isCorrect = await registrationService.submitAnswer(
                question?.[0],
                answer,
            );
            if (isCorrect === true) setCorrectAnswers((c) => c + 1);

            const total = questions.length || 10;
            const threshold = total > 0 ? Math.ceil(total * 0.7) : 7;

            if (currentQuestion < questions.length - 1) {
                setCurrentQuestion((i) => i + 1);
            } else {
                const finalCorrect =
                    correctAnswers + (isCorrect === true ? 1 : 0);
                if (finalCorrect >= threshold) {
                    // Quiz passed — finish registration and go to UCP
                    navigate("/ucp/");
                } else {
                    // fail — reset quiz
                    setTestError(true);
                    setCurrentQuestion(0);
                    setCorrectAnswers(0);
                }
            }
        } catch (err) {
            console.error("Ошибка проверки ответа:", err);
            setError("Ошибка проверки ответа");
        } finally {
            setQuizSubmitting(false);
        }
    };

    const RegisterSubmit = async (event) => {
        event.preventDefault();
        setRegError(false);

        const formData = new FormData(event.target);
        const payload = Object.fromEntries(formData.entries());

        try {
            setRegisterSubmitting(true);
            const data = await registrationService.createAccount({
                login: payload.login,
                password: payload.password,
                re_password: payload.re_password,
                email: payload.email,
                regIP: ip,
            });

            if (data?.status === "OK") {
                // store legacy values
                if (data.jwt) localStorage.setItem("jwtToken", data.jwt);
                if (data.player) {
                    localStorage.setItem("user-login", data.player.login);
                    localStorage.setItem("user-password", data.player.password);
                    localStorage.setItem("user-id", data.player.id);
                    localStorage.setItem("user-email", data.player.email);
                }

                const targetEmail = payload.email;
                setEmail(targetEmail);

                // send verification code and move to confirmation stage
                try {
                    const sent =
                        await registrationService.sendVerifyCode(targetEmail);
                    if (sent === true) {
                        setStage(2);
                        setTargetTimer(60);
                    } else {
                        setRegError("Не удалось отправить код подтверждения");
                    }
                } catch (err) {
                    console.error("Ошибка отправки кода:", err);
                    setRegError("Ошибка отправки кода");
                }

                return;
            }

            // error from server
            setRegError(data?.message || "Ошибка регистрации");
        } catch (err) {
            console.error("Ошибка регистрации:", err);
            setRegError("Ошибка соединения с сервером");
        } finally {
            setRegisterSubmitting(false);
        }
    };

    const SubmitCode = async (event) => {
        event.preventDefault();
        setError(null);

        const formData = new FormData(event.target);
        const payload = Object.fromEntries(formData.entries());

        const code =
            (payload["pincode-1"] || "") +
            (payload["pincode-2"] || "") +
            (payload["pincode-3"] || "") +
            (payload["pincode-4"] || "") +
            (payload["pincode-5"] || "") +
            (payload["pincode-6"] || "");

        const targetEmail = email || localStorage.getItem("user-email");

        try {
            setCodeSubmitting(true);
            const ok = await registrationService.submitVerifyCode(
                targetEmail,
                code,
            );
            if (ok === true) {
                // move to quiz
                setStage(3);
            } else {
                setError("Неверный код подтверждения");
            }
        } catch (err) {
            console.error("Ошибка подтверждения кода:", err);
            setError("Ошибка сервера при подтверждении кода");
        } finally {
            setCodeSubmitting(false);
        }
    };

    const handleResendMail = async () => {
        const targetEmail = email || localStorage.getItem("user-email");
        if (targetTimer > 0 || !targetEmail) return;
        try {
            setResending(true);
            const result =
                await registrationService.sendVerifyCode(targetEmail);
            if (result === true) setTargetTimer(60);
        } catch (err) {
            console.error("Ошибка соединения:", err);
        } finally {
            setResending(false);
        }
    };

    // Таймер
    useEffect(() => {
        if (targetTimer > 0) {
            const timer = setTimeout(() => setTargetTimer((t) => t - 1), 1000);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [targetTimer]);

    // pincode handlers
    const handleInputChange = (e, index) => {
        const value = e.target.value;
        if (/^\d$/.test(value)) {
            const nextInput = document.getElementById(`pincode-${index + 1}`);
            if (nextInput) nextInput.focus();
        } else {
            e.target.value = "";
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace" && e.target.value === "") {
            const prevInput = document.getElementById(`pincode-${index - 1}`);
            if (prevInput) prevInput.focus();
        }
    };

    if (loading && stage === 3)
        return (
            <div className="app-container">
                <div
                    className="main-container main-container--quest"
                    style={{ minHeight: "100vh" }}
                >
                    <RegSkeleton />
                </div>
            </div>
        );

    return (
        <AuthLayout>
            <div className="register-container">
                {stage === 1 && (
                    <>
                        <span className="register-title">РЕГИСТРАЦИЯ</span>
                        {regError && (
                            <span className="auth-error-span">{regError}</span>
                        )}
                        <form
                            onSubmit={RegisterSubmit}
                            className="register-form"
                        >
                            <input
                                className="register-input-form"
                                placeholder="Имя аккаунта*"
                                type="text"
                                name="login"
                                minLength={5}
                                maxLength={20}
                                onChange={handleChangeUcpName}
                                value={ucpName}
                            />
                            <input
                                className="register-input-form"
                                placeholder="Email*"
                                type="email"
                                name="email"
                                maxLength={36}
                                minLength={6}
                            />
                            <input
                                className="register-input-form"
                                placeholder="Пароль*"
                                type="password"
                                name="password"
                                maxLength={24}
                                minLength={7}
                            />
                            <input
                                className="register-input-form"
                                placeholder="Подтверждение пароля*"
                                type="password"
                                name="re_password"
                                maxLength={24}
                                minLength={7}
                            />
                            <input type="hidden" name="regIP" value={ip} />
                            <button
                                className={`register-button-next has-inline-loader ${
                                    isRegisterSubmitting ? "is-loading" : ""
                                }`}
                                type="submit"
                                disabled={isRegisterSubmitting}
                                aria-busy={isRegisterSubmitting}
                            >
                                <span className="inline-loader__label">
                                    ЗАРЕГИСТРИРОВАТЬСЯ
                                </span>
                                {isRegisterSubmitting ? (
                                    <span
                                        className="btn__loader inline-loader"
                                        aria-label="Загрузка"
                                    >
                                        <span className="btn__loader-dot"></span>
                                        <span className="btn__loader-dot"></span>
                                        <span className="btn__loader-dot"></span>
                                    </span>
                                ) : null}
                            </button>
                        </form>
                    </>
                )}

                {stage === 2 && (
                    <>
                        <span className="register-title">
                            ПОДТВЕРЖДЕНИЕ EMAIL
                        </span>
                        <span className="reg-span-timer">
                            На электронную почту вам было выслано письмо
                            подтверждения. Введите код из письма.
                        </span>
                        {error && (
                            <span className="auth-error-span">{error}</span>
                        )}
                        <form onSubmit={SubmitCode} className="register-form">
                            <div className="code-input-container">
                                {[...Array(6)].map((_, i) => (
                                    <input
                                        key={i}
                                        id={`pincode-${i + 1}`}
                                        className="code-input-number"
                                        maxLength={1}
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        type="tel"
                                        name={`pincode-${i + 1}`}
                                        onChange={(e) =>
                                            handleInputChange(e, i + 1)
                                        }
                                        onKeyDown={(e) =>
                                            handleKeyDown(e, i + 1)
                                        }
                                    />
                                ))}
                            </div>

                            <span className="reg-span-timer">
                                {targetTimer > 0
                                    ? `Вы сможете переотправить письмо через ${targetTimer} секунд`
                                    : "Вы можете переотправить письмо."}
                            </span>

                            <button
                                type="button"
                                className={`ucp-simple-blur-button has-inline-loader ${
                                    isResending ? "is-loading" : ""
                                }`}
                                onClick={handleResendMail}
                                disabled={targetTimer > 0 || isResending}
                                aria-busy={isResending}
                            >
                                <span className="inline-loader__label">
                                    ПЕРЕОТПРАВИТЬ ПИСЬМО
                                </span>
                                {isResending ? (
                                    <span
                                        className="btn__loader inline-loader"
                                        aria-label="Загрузка"
                                    >
                                        <span className="btn__loader-dot"></span>
                                        <span className="btn__loader-dot"></span>
                                        <span className="btn__loader-dot"></span>
                                    </span>
                                ) : null}
                            </button>

                            <button
                                className={`register-button-next has-inline-loader ${
                                    isCodeSubmitting ? "is-loading" : ""
                                }`}
                                type="submit"
                                disabled={isCodeSubmitting}
                                aria-busy={isCodeSubmitting}
                            >
                                <span className="inline-loader__label">
                                    ПОДТВЕРДИТЬ
                                </span>
                                {isCodeSubmitting ? (
                                    <span
                                        className="btn__loader inline-loader"
                                        aria-label="Загрузка"
                                    >
                                        <span className="btn__loader-dot"></span>
                                        <span className="btn__loader-dot"></span>
                                        <span className="btn__loader-dot"></span>
                                    </span>
                                ) : null}
                            </button>
                        </form>
                    </>
                )}

                {stage === 3 && (
                    <>
                        <span className="register-title">РЕГИСТРАЦИЯ</span>
                        {testError && (
                            <span className="auth-error-span">
                                Вы допустили более 3 ошибок! Пройдите тест
                                заново.
                            </span>
                        )}
                        {!question ? (
                            <p>Загрузка вопросов...</p>
                        ) : (
                            <>
                                <div>
                                    <p className="question-number">
                                        Вопрос {currentQuestion + 1}
                                    </p>{" "}
                                    <br />
                                    <p className="question-title">
                                        {JSON.stringify(question?.[1]).slice(
                                            1,
                                            -1,
                                        )}
                                    </p>
                                </div>
                                <form
                                    onSubmit={questionSubmit}
                                    className="register-form"
                                >
                                    <input
                                        type="hidden"
                                        name="question_id"
                                        value={JSON.stringify(question?.[0])}
                                    />
                                    <div className="answers-container">
                                        <ul>
                                            <li>
                                                <input
                                                    type="radio"
                                                    id="answer-1"
                                                    name="answer"
                                                    value="1"
                                                    defaultChecked
                                                />
                                                <label htmlFor="answer-1">
                                                    {JSON.stringify(
                                                        question?.[2],
                                                    ).slice(1, -1)}
                                                </label>
                                                <div className="check">
                                                    <div className="inside" />
                                                </div>
                                            </li>
                                            <li>
                                                <input
                                                    type="radio"
                                                    id="answer-2"
                                                    name="answer"
                                                    value="2"
                                                />
                                                <label htmlFor="answer-2">
                                                    {JSON.stringify(
                                                        question?.[3],
                                                    ).slice(1, -1)}
                                                </label>
                                                <div className="check">
                                                    <div className="inside" />
                                                </div>
                                            </li>
                                            <li>
                                                <input
                                                    type="radio"
                                                    id="answer-3"
                                                    name="answer"
                                                    value="3"
                                                />
                                                <label htmlFor="answer-3">
                                                    {JSON.stringify(
                                                        question?.[4],
                                                    ).slice(1, -1)}
                                                </label>
                                                <div className="check">
                                                    <div className="inside" />
                                                </div>
                                            </li>
                                        </ul>
                                    </div>
                                    <button
                                        className={`register-button-next has-inline-loader ${
                                            isQuizSubmitting ? "is-loading" : ""
                                        }`}
                                        type="submit"
                                        disabled={isQuizSubmitting}
                                        aria-busy={isQuizSubmitting}
                                    >
                                        <span className="inline-loader__label">
                                            СЛЕДУЮЩИЙ ВОПРОС
                                        </span>
                                        {isQuizSubmitting ? (
                                            <span
                                                className="btn__loader inline-loader"
                                                aria-label="Загрузка"
                                            >
                                                <span className="btn__loader-dot"></span>
                                                <span className="btn__loader-dot"></span>
                                                <span className="btn__loader-dot"></span>
                                            </span>
                                        ) : null}
                                    </button>
                                </form>
                            </>
                        )}
                    </>
                )}
            </div>
        </AuthLayout>
    );
}
